import { NavLink, useNavigate } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import {
  Briefcase,
  Settings,
  LogOut,
  ChevronRight,
  Zap,
  BarChart2,
  Shield,
  HelpCircle,
  X,
} from 'lucide-react'
import useAuthStore from '../../store/authStore'
import useOrgStore from '../../store/orgStore'
import { PlanBadge } from '../ui/Badge'
import { formatNumber } from '../../lib/utils'
import { getEffectivePlan, getSubscriptionPlan, isManualPlanOverride } from '../../lib/subscription'
import { useLocale } from '../../i18n/LocaleProvider'

const navItems = [
  {
    labelKey: 'jobs',
    path: '/dashboard',
    icon: Briefcase,
    end: true,
  },
  {
    labelKey: 'settings',
    path: '/dashboard/settings',
    icon: Settings,
  },
]

const adminItems = [
  {
    labelKey: 'adminPanel',
    path: '/dashboard/admin',
    icon: Shield,
  },
]

function getUsageBarState(current, max, percent) {
  const numericCurrent = Number(current) || 0
  const numericMax = Number(max)
  const numericPercent = Number(percent) || 0
  const unlimited = numericMax === -1
  const overLimit = !unlimited && numericCurrent > numericMax
  const clampedPercent = unlimited
    ? 0
    : Math.max(0, Math.min(numericPercent, 100))

  const colorClass =
    overLimit || numericPercent >= 90
      ? 'bg-red-500'
      : numericPercent >= 70
      ? 'bg-yellow-500'
      : 'bg-brand-500'

  return {
    overLimit,
    clampedPercent,
    colorClass,
  }
}

function UsageMeter({ label, current, max, percent, overLimitLabel }) {
  const { overLimit, clampedPercent, colorClass } = getUsageBarState(
    current,
    max,
    percent
  )

  return (
    <div>
      <div className="flex items-center justify-between gap-2 text-xs text-gray-500 mb-1">
        <div className="flex items-center gap-1.5 min-w-0">
          <span className="truncate">{label}</span>
          {overLimit && (
            <span className="inline-flex shrink-0 items-center rounded-full bg-red-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-red-700">
              {overLimitLabel}
            </span>
          )}
        </div>
        <span className="font-medium shrink-0">
          {formatNumber(current)}/{formatNumber(max)}
        </span>
      </div>
      <div className="w-full h-1.5 overflow-hidden rounded-full bg-gray-200">
        <div
          className={`h-1.5 rounded-full transition-all ${colorClass}`}
          style={{ width: `${clampedPercent}%` }}
        />
      </div>
    </div>
  )
}

export function Sidebar({ mobile = false, onClose }) {
  const { user, logout } = useAuthStore()
  const { usage, org } = useOrgStore()
  const navigate = useNavigate()
  const { t } = useLocale()

  const accessPlan = getEffectivePlan({ user, org, usage })
  const subscriptionPlan = getSubscriptionPlan({ user, org, usage })
  const manualPlanOverride = isManualPlanOverride({ user, org, usage })
  const isAdmin = Boolean(user?.isPlatformAdmin)

  function handleLogout() {
    logout()
  }

  function handleUpgrade() {
    navigate('/dashboard/settings?tab=billing')
    if (onClose) onClose()
  }

  const sidebarContent = (
    <div className="flex flex-col h-full">
      <div className="flex items-center justify-between p-5 border-b border-gray-100">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-xl bg-brand-600 flex items-center justify-center">
            <Zap className="w-4 h-4 text-white" />
          </div>
          <div>
            <p className="font-bold text-gray-900 text-sm leading-tight">
              WorkflowPro
            </p>
            <p className="text-xs text-gray-500 leading-tight">
              {org?.name || t('authenticated.app.sidebar.productSubtitle')}
            </p>
          </div>
        </div>
        {mobile && (
          <button
            onClick={onClose}
            className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg"
          >
            <X className="w-5 h-5" />
          </button>
        )}
      </div>

      <nav className="flex-1 p-3 space-y-0.5 overflow-y-auto">
        {navItems.map((item) => {
          const Icon = item.icon
          const planForAccess = isAdmin ? 'admin' : subscriptionPlan
          const isLocked = item.plans && !item.plans.includes(planForAccess)

          return (
            <NavLink
              key={item.path}
              to={item.path}
              end={item.end}
              onClick={onClose}
              className={({ isActive }) =>
                isActive ? 'sidebar-item-active' : 'sidebar-item'
              }
            >
              <Icon className="w-4 h-4 shrink-0" />
              <span className="flex-1">{t(`authenticated.app.sidebar.${item.labelKey}`)}</span>
              {isLocked && (
                <span className="text-xs text-gray-400 bg-gray-100 px-1.5 py-0.5 rounded-md">
                  {t('authenticated.app.sidebar.upgrade')}
                </span>
              )}
            </NavLink>
          )
        })}

        {isAdmin && (
          <>
            <div className="pt-3 pb-1 px-3">
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">
                {t('authenticated.app.sidebar.admin')}
              </p>
            </div>
            {adminItems.map((item) => {
              const Icon = item.icon
              return (
                <NavLink
                  key={item.path}
                  to={item.path}
                  end={item.end}
                  onClick={onClose}
                  className={({ isActive }) =>
                    isActive ? 'sidebar-item-active' : 'sidebar-item'
                  }
                >
                  <Icon className="w-4 h-4 shrink-0" />
                  <span className="flex-1">{t(`authenticated.app.sidebar.${item.labelKey}`)}</span>
                </NavLink>
              )
            })}
          </>
        )}

        <div className="pt-3 pb-1 px-3">
          <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">
            {t('authenticated.app.sidebar.help')}
          </p>
        </div>
        <NavLink
          to="/dashboard/help"
          onClick={onClose}
          className={({ isActive }) =>
            isActive ? 'sidebar-item-active' : 'sidebar-item'
          }
        >
          <HelpCircle className="w-4 h-4 shrink-0" />
          <span className="flex-1">{t('authenticated.app.sidebar.howItWorks')}</span>
        </NavLink>
      </nav>

      {isAdmin ? (
        <div className="p-3 mx-3 mb-3 bg-blue-50 rounded-xl border border-blue-100">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-1.5">
              <BarChart2 className="w-3.5 h-3.5 text-blue-500" />
              <span className="text-xs font-semibold text-blue-700">
                {t('authenticated.app.sidebar.platformAdmin')}
              </span>
            </div>
            <PlanBadge plan="admin" size="sm" />
          </div>
          <p className="text-xs text-blue-700">
            {t('authenticated.app.sidebar.adminAccess')}
          </p>
        </div>
      ) : (
        usage && (
          <div className="p-3 mx-3 mb-3 bg-gray-50 rounded-xl border border-gray-100">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-1.5">
                <BarChart2 className="w-3.5 h-3.5 text-gray-500" />
                <span className="text-xs font-semibold text-gray-600">
                  {t('authenticated.app.sidebar.usage')}
                </span>
              </div>
              <PlanBadge plan={accessPlan} size="sm" />
            </div>

            <div className="space-y-2">
              <UsageMeter
                label={t('authenticated.app.sidebar.activeJobs')}
                current={usage.activeJobs}
                max={usage.maxJobs}
                percent={usage.jobUsagePercent}
                overLimitLabel={t('authenticated.app.sidebar.overLimit')}
              />

              <UsageMeter
                label={t('authenticated.app.sidebar.cvsThisMonth')}
                current={usage.monthlyCVs}
                max={usage.maxCVsPerMonth}
                percent={usage.cvUsagePercent}
                overLimitLabel={t('authenticated.app.sidebar.overLimit')}
              />
            </div>

            {!manualPlanOverride && subscriptionPlan !== 'enterprise' && !isAdmin && (
              <button
                onClick={handleUpgrade}
                className="w-full mt-3 text-xs font-medium text-brand-600 hover:text-brand-700 flex items-center justify-center gap-1 py-1.5 hover:bg-brand-50 rounded-lg transition-colors"
              >
                <Zap className="w-3 h-3" />
                {t('authenticated.app.sidebar.upgradePlan')}
                <ChevronRight className="w-3 h-3" />
              </button>
            )}
          </div>
        )
      )}

      <div className="p-3 border-t border-gray-100">
        <div className="flex items-center gap-3 p-2 rounded-xl hover:bg-gray-50 transition-colors">
          <div className="w-8 h-8 rounded-full bg-brand-100 flex items-center justify-center shrink-0">
            <span className="text-xs font-bold text-brand-700">
              {user?.name?.charAt(0)?.toUpperCase() ||
                user?.email?.charAt(0)?.toUpperCase() ||
                '?'}
            </span>
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-gray-900 truncate">
              {user?.name || user?.email?.split('@')[0]}
            </p>
            <p className="text-xs text-gray-500 truncate">{user?.email}</p>
          </div>
          <button
            onClick={handleLogout}
            className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
            title={t('authenticated.app.sidebar.logOut')}
            aria-label={t('authenticated.app.sidebar.logOut')}
          >
            <LogOut className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  )

  if (mobile) {
    return (
      <AnimatePresence>
        <div className="fixed inset-0 z-50 lg:hidden">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute inset-0 bg-black bg-opacity-50"
            onClick={onClose}
          />
          <motion.div
            initial={{ x: '-100%' }}
            animate={{ x: 0 }}
            exit={{ x: '-100%' }}
            transition={{ type: 'spring', damping: 30, stiffness: 300 }}
            className="absolute left-0 top-0 bottom-0 w-72 bg-white shadow-2xl"
          >
            {sidebarContent}
          </motion.div>
        </div>
      </AnimatePresence>
    )
  }

  return (
    <div className="hidden lg:flex flex-col w-64 bg-white border-r border-gray-100 h-screen sticky top-0">
      {sidebarContent}
    </div>
  )
}
